import { getDefaultStore } from 'jotai';

import { getCachedAccessToken } from '@/lib/auth';
import { getEffectiveAuthProvider } from '@/lib/auth/effectiveAuthProvider';
import { isTauri } from '@/tauri/utils';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import {
  getExternalLaunchAuthToken,
  getLegacyReduxToken,
  getReduxAuthProvider,
} from '@/utils/http/getAuthTokenForHttpClient';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@/utils/logger/sanitizeError';
import { isCacheReadyProvider } from '@heidi/auth/predicates';
import { authCacheSuppressedAtom } from '@heidi/auth/state';

/**
 * Synchronous token read for direct consumers whose signatures
 * MUST stay sync — `Record<string, string>` header builders (SSE / streaming
 * / RPA), sync token accessors handed to non-React consumers, Tiptap command
 * handlers that return `true` (must be sync per the tiptap contract), and any
 * inline read inside a sync closure that would require caller-API changes to
 * accept a Promise.
 *
 * Same branch shape as `getAuthTokenForHttpClient()` (the async variant),
 * including the provider/platform allowlist gate. The only difference is what
 * the allowlisted branch does once gated in: this helper reads the cache's
 * sync accessor (`getCachedAccessToken()`) instead of launching a fetch.
 *
 * 0. **Suppression short-circuit.** When the auth cache is suppressed (the
 *    public chat embed), attach no bearer at all — not an external-launch
 *    token from a prior FHIR/MD launch, and not a redux-persist-rehydrated
 *    `auth.token`. The embed's contract is anonymous/public, and every
 *    interceptor consuming this helper guards the empty case
 *    (`token ? Bearer : ''`), so `undefined` yields no Authorization header.
 *
 * 1. **External-launch token presence gate.** FHIR launches and
 *    MD-restored sessions win here.
 *
 * 2. **Interim FHIR fallback.** Post-reload FHIR users have
 *    the external-launch token empty but `auth.token` rehydrated; read Redux
 *    directly. Removed when the FHIR readers migrate off `state.auth.token`.
 *
 * 3. **Provider/platform allowlisted → cache's sync accessor.**
 *    The cache is warmed app-wide for every provider (the scheduler's
 *    dispatcher reads Kinde, Keycloak, and Auth0-desktop sessions too), so a
 *    populated cache slot is not by itself evidence that provider's cache
 *    read path is safe to serve. `isCacheReadyProvider(getEffectiveAuthProvider(),
 *    isTauri())` gates on the same allowlist the async helper uses — only
 *    entries that have been explicitly cleared for cache reads take this
 *    branch. When allowlisted, returns `cached.token` if the slot is
 *    populated (no fetch, no expiry check — expired tokens flow to the
 *    caller so the BE 401 drives whatever error path they already have). If
 *    the slot is empty (pre-warmup, or post-`clearAuthCache`), falls through
 *    to the Redux read so the dual-run mirror keeps the caller supplied.
 *
 * 4. **Provider/platform NOT allowlisted → legacy Redux read.**
 *    No cache read at all for cohorts that haven't been cleared.
 *
 * The allowlist check and cache read (branches 3-4) are wrapped in a
 * dual-run fallback: an uncaught throw there is worse than a missing token —
 * an axios request interceptor throw rejects the request unsent, and
 * a Tiptap command throw propagates into the editor command chain — so any
 * throw is caught, logged, and served the Redux read instead of failing the
 * caller. Mirrors the async helper's dynamic-import catch block.
 *
 * ## What this helper is NOT
 *
 * **Not an active refresh.** The sync variant is a safety-net for consumers
 * that can't `await`. If a consumer genuinely needs a fresh token, adopt the
 * async variant (`getAuthTokenForHttpClient`) and refactor callers.
 *
 * **Not a logout signal.** A `null`/`undefined` return is never evidence the
 * user logged out — it can mean warm-up-in-progress or
 * pre-rehydration state. Consumers MUST NOT dispatch a logout on
 * this return; they should behave the same way they behave today when
 * `store.select.auth.getToken(state)` returns null — typically omit the
 * `Authorization` header and let the BE 401 flow through the existing error
 * handling.
 */
export function getAuthTokenForHttpClientSync(): string | null | undefined {
  if (getDefaultStore().get(authCacheSuppressedAtom)) {
    return undefined;
  }
  const externalToken = getExternalLaunchAuthToken();
  if (externalToken) {
    return externalToken;
  }
  if (getReduxAuthProvider() === AuthProvider.FHIR) {
    return getLegacyReduxToken();
  }
  try {
    if (!isCacheReadyProvider(getEffectiveAuthProvider(), isTauri())) {
      return getLegacyReduxToken();
    }
    const cachedToken = getCachedAccessToken();
    if (cachedToken !== null) {
      return cachedToken;
    }
    return getLegacyReduxToken();
  } catch (err) {
    // DUAL-RUN FALLBACK: an uncaught throw here (e.g. a module-init-order
    // edge in the require cycle this helper sits in) must not fail the
    // caller — serve the Redux token.
    logger.warn(
      '[auth-cache] getAuthTokenSync path failed, falling back to Redux token',
      sanitizeError(err)
    );
    return getLegacyReduxToken();
  }
}
